import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import PageHeader from "../Common/PageHeader";
import WeatherIcon from "../../assets/icons/icon_weather_header_c-01.svg2";
import WhiteButton from "../Common/WhiteButton";
import {
  faCircleNotch,
  faGripLines,
  faInfo,
  faInfoCircle,
  faPlus,
  faRemove,
  faSave,
  faSpinner,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ColorPicker from "./ColorPicker";
import { proxyAPI } from "../proxyAPI";
import { toast } from "react-toastify";
import { Button, Col, Row } from "react-bootstrap";
import {
  renderCondition,
  WeatherAlertOperators,
  WeatherAlertConditions,
} from "./WeatherAlertConstants";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import InputLabel from "../Builder/nodes/Logic/Inputs/InputLabel";
import MyIcon from "../Common/MyIcon";
import WeatherConditionValueSelector from "./WeatherConditionValueSelector";
import JsonCollapse from "../Builder/Components/JsonCollapse";

export default function WeatherAlert({ selectedTab, setWeatherAlertData }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [projectList, setProjectList] = useState([]);
  const { alertID } = useParams();

  const [identifierType, setIdentifierType] = useState({});
  const [identifier, setIdentifier] = useState({});
  const [detailsMap, setDetailsMap] = useState({});
  const [countries, setCountries] = useState([
    { value: "GB", label: "United Kingdom", selected: true },
  ]); // later on fetch from api instead
  const [geoUnitTypes, setGeoUnitTypes] = useState([
    { value: "country", label: "Whole Country" },
    { value: "region", label: "Region" },
    { value: "county", label: "County" },
  ]);
  const [geoUnitGroups, setGeoUnitGroups] = useState([
    { value: "country", label: "Country" },
    { value: "region", label: "Region" },
    { value: "county", label: "County" },
  ]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedCountyRegion, setSelectedCountyRegion] = useState(null);
  const [selectedGroupBy, setSelectedGroupBy] = useState(null);
  const [countyOrGroupPage, setCountyOrGroupPage] = useState(0);
  const [countyOrGroupOptions, setCountyOrGroupOptions] = useState([]);
  const totalCountyOrGroup = useRef(0);
  const [countyOrGroupSearch, setCountyOrGroupSearch] = useState("");
  const [isCountyRegionLoading, setIsCountyRegionLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Alert form state
  const [alertData, setAlertData] = useState({
    ID: 0,
    ShapeId: 0,
    GroupName: "",
    Active: false,
    Priority: 1,
    Description: "",
    Stop: false,
    CountryIso: "",
    GeoUnitType: "",
    GeoUnitGroup: "",
  });

  const [projects, setProjects] = useState([
    {
      ProjectId: null,
      InputArgumentUuid: "",
      selectedProject: {},
    },
  ]);

  const [newProject, setNewProject] = useState({
    ID: null,
    InputArgumentUuid: "",
    selectedProject: {},
  });

  // Alert condition state
  const [conditions, setConditions] = useState([
    {
      ConditionDetails: [
        {
          TimeOfDay: "both",
          Condition: "",
          ConditionOperator: "=",
          ConditionValue: [],
          conditionHelper: "",
          conditionIcon: "",
          conditionUnit: "",
          conditionOptions: [],
          selectedCondition: {},
        },
      ],
      DaysAhead: [0, 1, 2, 3, 4, 5, 6],
      Condition: "",
      ConditionOperator: "=",
      ConditionValue: 0,
      Description: "",
      Color: "#FF0000",
      Order: 0,
      conditionHelper: "",
      conditionIcon: "",
      conditionUnit: "",
      conditionOptions: [],
      selectedCondition: {},
    },
  ]);
  const [newCondition, setNewCondition] = useState({
    ConditionDetails: [
      {
        TimeOfDay: "both",
        Condition: "",
        ConditionOperator: "=",
        ConditionValue: [],
        conditionHelper: "",
        conditionIcon: "",
        conditionUnit: "",
        conditionOptions: [],
        selectedCondition: {},
      },
    ],
    DaysAhead: [0, 1, 2, 3, 4, 5, 6],
    Description: "",
    Color: "#FF0000",
    Order: 0,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const transformAlertData = (data) => {
    const url =
      "/geo/" +
      (data.GroupName === ""
        ? "shapes?id=" + data.ShapeId
        : "groups?search=" + data.GroupName);

    proxyAPI({
      prefix: "logic/workflows/api",
      url: url,
      setLoading,
    })
      .then((shapeOrGroup) => {
        if (shapeOrGroup) {
          setSelectedCountyRegion(null);
          if (data.ShapeId > 0) {
            setSelectedCountyRegion({
              value: shapeOrGroup.shapes[0]?.ID,
              label: shapeOrGroup.shapes[0]?.Name,
            });
          } else if (data.GroupName !== "") {
            setSelectedCountyRegion({
              value: shapeOrGroup.groups[0]?.Item,
              label: shapeOrGroup.groups[0]?.Item,
            });
          }

          setRefreshKey((prev) => prev + 1);
        }
      })
      .catch((error) => {
        console.error("Error fetching shape or county data:", error);
      });

    return {
      ID: data.ID || 0,
      ShapeId:
        identifierType?.value === "shapes" ? parseInt(identifier?.value) : 0,
      GroupName: identifierType?.value === "groups" ? identifier?.value : "",
      Active: data.Active ?? false,
      Priority: data.Priority || 1,
      Description: data.Description || "",
      Stop: data.Stop ?? false,
    };
  };

  const transformProjects = (projectsArray = [], plist = []) => {
    var list = plist.length > 0 ? plist : projectList;
    setRefreshKey((prev) => prev + 1);
    return projectsArray.map((proj, index) => {
      return {
        ProjectId: proj.ProjectId || null,
        InputArgumentUuid: proj.InputArgumentUuid || "",
        selectedProject: list.find((p) => p.ID === proj.ProjectId) || {},
      };
    });
  };

  const getProjectDetails = async (projectId) => {
    return proxyAPI({
      prefix: "logic/workflows/api",
      url: "project/" + projectId,
      setLoading,
    });
  };

  const transformConditions = (conditionsArray = []) => {
    return conditionsArray
      .sort((a, b) => a.Order - b.Order)
      .map((cond, index) => ({
        ID: cond.ID || 0,
        Description: cond.Description || "",
        Color: cond.Color || "#FF0000",
        Order: index,
        ConditionDetails: cond.ConditionDetails.map((cDetails) => ({
          ...cDetails,
          conditionHelper:
            WeatherAlertConditions.find((c) => c.key === cDetails.Condition)
              ?.description || "",
          conditionIcon:
            WeatherAlertConditions.find((c) => c.key === cDetails.Condition)
              ?.icon || "",
          conditionUnit:
            WeatherAlertConditions.find((c) => c.key === cDetails.Condition)
              ?.unit || "",
          conditionOptions:
            WeatherAlertConditions.find((c) => c.key === cDetails.Condition)
              ?.options || [],
          selectedCondition:
            WeatherAlertConditions.find((c) => c.key === cDetails.Condition) ||
            {},
        })),
      }));
  };

  const getCountiesOrGroups = async (search, page) => {
    try {
      var searchType = "groups";

      if (selectedArea?.value == "county") {
        searchType = "shapes";
      }

      var queryParams = `search=${search}`;
      if (selectedCountry && selectedCountry?.value !== "") {
        queryParams += `&country=${selectedCountry?.value}`;
      }

      queryParams += `&page=${page}&perPage=50`;

      const data = await proxyAPI({
        prefix: "logic/workflows/api",
        url: "geo/" + searchType + "?" + queryParams,
        setLoading,
      });

      var options = [];
      if (searchType === "shapes" && data.shapes) {
        options = data.shapes.map((gs) => {
          return {
            value: gs.ID,
            label: gs.Name,
          };
        });
      } else if (searchType == "groups" && data.groups) {
        options = data.groups.map((group) => {
          return {
            value: group.Item,
            label: group.Item,
          };
        });
      }
      if (page == 0) {
        setCountyOrGroupOptions([]);
      }

      setCountyOrGroupOptions((prev) => {
        const updatedOptions = [...prev, ...options];
        return updatedOptions;
      });

      totalCountyOrGroup.current = data.total;

      if (
        selectedCountyRegion?.value &&
        search == "" &&
        !options.some((opt) => opt.value === selectedCountyRegion.value)
      ) {
        options = [identifier, ...options];
      }

      return options;
    } catch (error) {
      console.error("Error fetching counties/group", error);
      return [];
    }
  };

  const getProjects = async (inputValue) => {
    try {
      const response = await proxyAPI({
        prefix: "logic/workflows/api",
        url: `list-projects?${inputValue.toString()}&withArgTypes=json&withArgValues=json`,
        setLoading,
      });

      if (response.projects) {
        const filteredProjects = response.projects.filter(
          (p) => !projects.some((proj) => proj.ProjectId === p.ID)
        );

        setProjectList(filteredProjects);
        return filteredProjects.map((project) => {
          const disabled = projects.some((p) => p.ProjectId === project.ID);
          return {
            value: project.ID,
            label: project.Label,
            disabled: disabled,
            data: project,
          };
        });
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      return [];
    }
  };

  // Error state
  const [errors, setErrors] = useState({});

  // Predefined options for dropdowns
  const conditionNames = WeatherAlertConditions;

  const operators = WeatherAlertOperators;

  const selectedCondition =
    conditionNames.find((c) => c.key === newCondition.Condition) || {};

  const validateForm = (data) => {
    const newErrors = {};

    if (!data.Priority || parseInt(data.Priority) < 1) {
      newErrors.priority = "Priority must be a positive number";
    }

    if (!data.Description) {
      newErrors.Description = "Description is required";
    }

    Object.keys(newErrors).map((k) => {
      toast.error(newErrors[k]);
    });
    console.log("ERRORS", newErrors);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAlertChange = (field, value) => {
    const updatedData = {
      ...alertData,
      [field]: value,
    };
    setAlertData(updatedData);

    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  const handleProjectChange = (field, value, index) => {
    if (typeof index != "undefined") {
      projects[index][field] = value;
      if (field === "ProjectId") {
        projects[index].selectedProject =
          projectList.find((p) => p.ID === value) || {};
      }
      setProjects([...projects]);
    } else {
      var data = (prev) => {
        if (field === "ProjectId") {
          return {
            ...prev,
            [field]: value,
            selectedProject: projectList.find((p) => p.ID === value) || {},
          };
        }
        return {
          ...prev,
          [field]: value,
        };
      };
      setNewProject(data);
    }
  };

  const handleConditionChange = (field, value, index) => {
    if (typeof index != "undefined") {
      conditions[index][field] = value;
      setConditions([...conditions]);
    } else {
      setNewCondition((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };
  const handleConditionDetailChange = (
    detailIndex,
    field,
    value,
    conditionIndex
  ) => {
    if (typeof conditionIndex === "number") {
      // Editing existing condition in the conditions array
      setConditions((prevConditions) => {
        const updatedConditions = [...prevConditions];

        // Update helper info when condition changes
        if (field === "Condition") {
          const selectedCondition =
            WeatherAlertConditions.find((c) => c.key === value) || {};

          // Update the nested condition detail
          updatedConditions[conditionIndex].ConditionDetails[detailIndex] = {
            ...updatedConditions[conditionIndex].ConditionDetails[detailIndex],
            [field]: value,
            conditionHelper: selectedCondition.description || "",
            conditionIcon: selectedCondition.icon || "",
            conditionUnit: selectedCondition.unit || "",
            conditionOptions: selectedCondition.options || [],
            selectedCondition: selectedCondition,
          };
        } else {
          // For other fields, just update that field
          updatedConditions[conditionIndex].ConditionDetails[detailIndex] = {
            ...updatedConditions[conditionIndex].ConditionDetails[detailIndex],
            [field]: value,
          };
        }

        return updatedConditions;
      });
    } else {
      // Handling new condition
      setNewCondition((prevState) => {
        const updatedConditionDetails = [...prevState.ConditionDetails];

        // Update helper info when condition changes
        if (field === "Condition") {
          const selectedCondition =
            WeatherAlertConditions.find((c) => c.key === value) || {};
          updatedConditionDetails[detailIndex] = {
            ...updatedConditionDetails[detailIndex],
            [field]: value,
            conditionHelper: selectedCondition.description || "",
            conditionIcon: selectedCondition.icon || "",
            conditionUnit: selectedCondition.unit || "",
            conditionOptions: selectedCondition.options || [],
            selectedCondition: selectedCondition,
          };
        } else {
          updatedConditionDetails[detailIndex] = {
            ...updatedConditionDetails[detailIndex],
            [field]: value,
          };
        }

        return {
          ...prevState,
          ConditionDetails: updatedConditionDetails,
        };
      });
    }
  };

  const handleRemoveCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const addCondition = (e) => {
    e.preventDefault();
    if (validateCondition()) {
      setConditions((prev) => [...prev, newCondition]);
      setNewCondition({
        ConditionDetails: [
          {
            TimeOfDay: "both",
            Condition: "",
            ConditionOperator: "=",
            ConditionValue: [0],
            conditionHelper: "",
            conditionIcon: "",
            conditionUnit: "",
            conditionOptions: [],
            selectedCondition: {},
          },
        ],
        Description: "",
        Color: "#FF0000",
        Order: 0,
      });
      setErrors({ ...errors, condition: "" });
    }
  };

  const addConditionDetail = (conditionIndex) => {
    if (typeof conditionIndex === "number") {
      // Editing an existing condition within the conditions array
      setConditions((prevConditions) => {
        const updatedConditions = [...prevConditions];

        // Add a new condition detail to the specified condition
        updatedConditions[conditionIndex].ConditionDetails = [
          ...updatedConditions[conditionIndex].ConditionDetails,
          {
            TimeOfDay: "both",
            Condition: "",
            ConditionOperator: "=",
            ConditionValue: [0],
            conditionHelper: "",
            conditionIcon: "",
            conditionUnit: "",
            conditionOptions: [],
            selectedCondition: {},
          },
        ];

        return updatedConditions;
      });
    } else {
      // Adding to the newCondition
      setNewCondition((prevState) => ({
        ...prevState,
        ConditionDetails: [
          ...prevState.ConditionDetails,
          {
            TimeOfDay: "both",
            Condition: "",
            ConditionOperator: "=",
            ConditionValue: [0],
            conditionHelper: "",
            conditionIcon: "",
            conditionUnit: "",
            conditionOptions: [],
            selectedCondition: {},
          },
        ],
      }));
    }
  };

  const removeConditionDetail = (indexToRemove, conditionIndex) => {
    if (typeof conditionIndex === "number") {
      // Removing from an existing condition in the conditions array
      setConditions((prevConditions) => {
        const updatedConditions = [...prevConditions];

        // Remove the condition detail at the specified indices
        updatedConditions[conditionIndex].ConditionDetails = updatedConditions[
          conditionIndex
        ].ConditionDetails.filter((_, index) => index !== indexToRemove);

        return updatedConditions;
      });
    } else {
      // Removing from newCondition
      setNewCondition((prevState) => ({
        ...prevState,
        ConditionDetails: prevState.ConditionDetails.filter(
          (_, index) => index !== indexToRemove
        ),
      }));
    }
  };

  const validateCondition = () => {
    // if (newCondition.ConditionDetails.length < 0) {
    //   setErrors({ ...errors, condition: 'All condition fields are required' });
    //   return false;
    // }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm(alertData) && conditions.length > 0) {
      const data = await proxyAPI({
        prefix: "logic/workflows/api",
        url: "/geo/alerts/rules",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          ...alertData,
          Conditions: conditions,
          Projects: [...projects],
        },
        setLoading,
      });

      if (data?.error) {
        toast.error(data.error);
        return;
      } else {
        toast.success("Weather Alert " + (isEdit ? "Updated" : "Created"));
      }

      if (data?.ID) {
        if (!isEdit) {
          toast.success("redirecting ...");
        }
        navigate("/library/weather-alerts/" + data.ID, { state: data });
      }
    } else if (conditions.length === 0) {
      toast.error("At least one condition is required");
      setErrors((prev) => ({
        ...prev,
        conditions: "At least one condition is required",
      }));
    }
  };

  // Add these validation checks
  const isConditionValid = Boolean(
    newCondition.Description.trim() &&
      newCondition.ConditionDetails.length > 0 &&
      newCondition.ConditionDetails.every(
        (detail) =>
          detail.TimeOfDay &&
          detail.Condition &&
          Array.isArray(detail.ConditionValue) &&
          detail.ConditionValue.length > 0 &&
          detail.ConditionValue.every(
            (x) => x !== null && x !== undefined && x !== ""
          ) &&
          detail.ConditionOperator
      )
  );

  const isAlertValid =
    selectedCountry?.value &&
    selectedArea?.value &&
    alertData.Description &&
    parseInt(alertData.Priority) >= 1 &&
    conditions.length > 0;

  useEffect(() => {
    // Check if this is an edit page
    if (typeof alertID !== "undefined") {
      const fetchData = async () => {
        try {
          const data = await proxyAPI({
            prefix: "logic/workflows/api",
            url: "list-projects?withArgTypes=json&withArgValues=json",
            headers: {
              "Content-Type": "application/json",
            },
            setLoading,
          });

          if (data.projects) {
            // Filter out projects that are already in the `projects` array
            const filteredProjects = data.projects.filter((p) => {
              return !projects.some((proj) => proj.ProjectId === p.ID);
            });

            const projectList = filteredProjects || [];

            const response = await proxyAPI({
              prefix: "logic/workflows/api",
              url: `/geo/alerts/rules/${alertID}`,
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
              setLoading,
            });

            if (response?.error) {
              toast.error(response.error);
              navigate("/library/weather-alerts");
              return;
            }

            if (response.GroupName !== "") {
              setIdentifierType({
                value: "groups",
                label: "Group",
              });
            } else {
              setIdentifierType({
                value: "shapes",
                label: "County",
              });
            }
            // Transform and set the data
            setAlertData(transformAlertData(response));
            setConditions(transformConditions(response.Conditions || []));
            setProjects(
              transformProjects(response.Projects || [], projectList)
            );
            setSelectedCountry(() =>
              countries.find((c) => c.value == response.CountryIso)
            );
            setSelectedArea(() =>
              geoUnitTypes.find((gt) => gt.value == response.GeoUnitType)
            );
            setSelectedGroupBy(() =>
              geoUnitGroups.find((gg) => gg.value == response.GeoUnitGroup)
            );

            setIsEdit(true);
          } else {
            navigate("/library/weather-alerts");
          }
        } catch (error) {
          toast.error("Error fetching data", error);
          console.error("Error fetching data:", error);
        }
      };

      fetchData();
    }

    // Remove empty or invalid conditions and projects
    setConditions((prevConditions) =>
      prevConditions.filter((condition) => condition.Condition !== "")
    );

    setProjects((prevProjects) =>
      prevProjects.filter((proj) => proj.ProjectId !== null)
    );
  }, [navigate]);

  useEffect(() => {
    projects.forEach((proj) => {
      // Only fetch if we haven't already
      if (!detailsMap[proj.ProjectId] && proj.ProjectId) {
        getProjectDetails(proj.ProjectId).then((data) => {
          if (data) {
            setDetailsMap((prev) => ({
              ...prev,
              [proj.ProjectId]: data,
            }));
          }
        });
      }
    });
  }, [projects]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(countyOrGroupSearch);
      setCountyOrGroupPage(0); // Reset to first page on new search
    }, 300);

    return () => clearTimeout(timer);
  }, [countyOrGroupSearch]);

  useEffect(() => {
    const loadOptions = async () => {
      setIsCountyRegionLoading(true);
      try {
        const newOptions = await getCountiesOrGroups(
          debouncedSearch,
          countyOrGroupPage
        );

        // Replace options if it's page 0, otherwise append
        if (countyOrGroupPage === 0) {
          setCountyOrGroupOptions(newOptions);
        } else {
          setCountyOrGroupOptions((prev) => {
            // Create a map to check for duplicates
            const uniqueMap = new Map(prev.map((item) => [item.value, item]));

            // Add new options if they don't already exist
            newOptions.forEach((option) => {
              if (!uniqueMap.has(option.value)) {
                uniqueMap.set(option.value, option);
              }
            });

            // Convert map back to array
            return Array.from(uniqueMap.values());
          });
        }
      } catch (error) {
        console.error("Error loading options:", error);
      } finally {
        setIsCountyRegionLoading(false);
      }
    };

    loadOptions();
  }, [
    debouncedSearch,
    countyOrGroupPage,
    selectedArea?.value,
    selectedCountry?.value,
  ]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(conditions);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    const updated = reordered.map((cond, index) => ({
      ...cond,
      Order: index,
    }));
    setConditions(updated);
  };

  useEffect(() => {
    if (!setWeatherAlertData) return;
    setWeatherAlertData({
      ...alertData,
      Conditions: conditions,
      Projects: [...projects],
      CountryIso: selectedCountry?.value,
      GeoUnitType: selectedArea?.value,
      GeoUnitGroup: selectedGroupBy?.value || "",
      ShapeId:
        selectedArea?.value === "county"
          ? parseInt(selectedCountyRegion?.value, 10)
          : 0,
      GroupName:
        selectedArea?.value === "region" ? selectedCountyRegion?.value : "",
    });
  }, [
    alertData,
    conditions,
    projects,
    selectedCountyRegion,
    selectedCountry,
    selectedArea,
    selectedGroupBy,
  ]);

  return (
    <div className="">
      <form onSubmit={handleSubmit} className="row g-3">
        <PageHeader
          Icon={WeatherIcon}
          heading="Weather Alerts"
          subheading={isEdit ? "Edit Weather Alert" : "Add a new weather alert"}
        >
          <div className="d-flex flex-wrap gap-2">
            <div className="row">
              <div className="col-12 my-4">
                <WhiteButton
                  as="button"
                  type="submit"
                  onClick={() => {
                    setAlertData((prev) => ({
                      ...prev,
                      ["CountryIso"]: selectedCountry?.value,
                      ["GeoUnitType"]: selectedArea?.value,
                      ["GeoUnitGroup"]: selectedGroupBy?.value || "",
                      ["ShapeId"]:
                        selectedArea?.value === "county"
                          ? parseInt(selectedCountyRegion?.value)
                          : 0,
                      ["GroupName"]:
                        selectedArea?.value === "region"
                          ? selectedCountyRegion?.value
                          : "",
                    }));
                  }}
                  icon={() => {
                    return (
                      <FontAwesomeIcon
                        className="mt-2"
                        fontSize={"xs"}
                        icon={faSave}
                      />
                    );
                  }}
                  heading="Save Weather Alert"
                  subheading="Save the alert with all conditions"
                  disabled={!isAlertValid}
                />
              </div>
            </div>
          </div>
        </PageHeader>
        <div>
          {loading && (
            <div className="position-fixed top-50 start-50 translate-middle text-grey">
              <FontAwesomeIcon icon={faCircleNotch} spin /> Loading...
            </div>
          )}
        </div>
        <Row>
          <Col>
            <div className="p-4">
              <div className="my-3 p-3 rounded-3">
                <div className="row">
                  <div className="col-12">
                    <h4>Alert Configuration</h4>
                    {errors.conditions && (
                      <div className="text-danger">{errors.conditions}</div>
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <InputLabel>Select Country</InputLabel>
                    <Select
                      isClearable
                      value={selectedCountry}
                      onChange={(v) => {
                        setSelectedGroupBy(null);
                        setSelectedCountyRegion(null);
                        setSelectedArea(null);
                        setSelectedCountry(v);
                      }}
                      options={countries}
                    />
                  </div>
                  <div className={"col-md-3"} hidden={!selectedCountry}>
                    <InputLabel>Select Area</InputLabel>
                    <Select
                      isClearable
                      isDisabled={!selectedCountry}
                      value={selectedArea}
                      onChange={(v) => {
                        setSelectedGroupBy(null);
                        setSelectedCountyRegion(null);
                        setCountyOrGroupOptions([]);
                        setCountyOrGroupPage(0);
                        setSelectedArea(v);
                      }}
                      options={geoUnitTypes}
                    />
                  </div>
                  <div
                    className="col-md-3"
                    hidden={
                      !selectedCountry ||
                      !selectedArea ||
                      selectedArea?.value === "country"
                    }
                  >
                    <InputLabel>Select {selectedArea?.value}</InputLabel>
                    <Select
                      key={`${selectedArea?.value}-select`}
                      value={selectedCountyRegion}
                      onChange={(v) => {
                        setSelectedCountyRegion(v);
                        console.log("Selected County/Region:", v);
                      }}
                      options={countyOrGroupOptions}
                      isLoading={isCountyRegionLoading}
                      isClearable
                      isSearchable
                      onInputChange={(value) => {
                        setCountyOrGroupSearch(value);
                      }}
                      filterOption={(option, inputValue) => {
                        return true;
                      }}
                      onMenuScrollToBottom={() => {
                        if (
                          countyOrGroupOptions.length <
                          totalCountyOrGroup.current
                        ) {
                          setCountyOrGroupPage((prev) => prev + 1);
                        }
                      }}
                    />
                  </div>
                  <div
                    className="col-md-3 d-none"
                    hidden={selectedArea?.value === "county"}
                  >
                    <InputLabel>Group By</InputLabel>
                    <Select
                      isClearable
                      value={selectedGroupBy}
                      onChange={(v) => {
                        setSelectedGroupBy(v);
                      }}
                      filterOption={(opt) => {
                        if (selectedArea?.value === "region") {
                          return opt?.value !== "country";
                        }
                        return true;
                      }}
                      options={geoUnitGroups}
                    />
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-12">
                    <Input
                      inputType="textArea"
                      label="Alert Description"
                      value={[alertData.Description]}
                      onChange={(values) =>
                        handleAlertChange("Description", values[0])
                      }
                      options={[{ value: "", label: "" }]}
                    />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-3">
                    <Input
                      type="number"
                      label="Priority"
                      value={[alertData.Priority]}
                      onChange={(values) =>
                        handleAlertChange("Priority", parseInt(values[0]))
                      }
                      options={[{ value: "", label: "" }]}
                      min={1}
                      max={10}
                    />
                  </div>
                  <div className="col-md-1 mt-4">
                    <Input
                      inputType="checkbox"
                      label="Active"
                      value={[alertData.Active ? true : false]}
                      onChange={(values) =>
                        handleAlertChange("Active", values[0] === true)
                      }
                      options={[
                        { value: true, label: "Yes" },
                        { value: false, label: "No" },
                      ]}
                    />
                  </div>
                  <div className="col-md-3 mt-4">
                    <Input
                      inputType="checkbox"
                      label="Stop"
                      value={[alertData.Stop ? true : false]}
                      onChange={(values) =>
                        handleAlertChange("Stop", values[0] === true)
                      }
                      options={[
                        { value: true, label: "Yes" },
                        { value: false, label: "No" },
                      ]}
                    />
                    <span className="text-muted ps-4">
                      If checked, no alert will be triggered after this
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className="p-4">
              <div className="my-3 g-3 p-3">
                <div className="col-12">
                  <h4>Alert Conditions</h4>
                  {errors.condition && (
                    <div className="text-danger">{errors.condition}</div>
                  )}
                </div>
                {conditions.length > 0 && (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="conditionsList">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {conditions.map((condition, index) => (
                            <Draggable
                              key={index.toString()}
                              draggableId={`cond-${index}`}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className="col-12 p-3 border rounded mb-3 position-relative bg-light"
                                  style={{
                                    ...provided.draggableProps.style,
                                    marginBottom: "8px",
                                    height: "auto",
                                  }}
                                >
                                  <div className="d-flex">
                                    <div
                                      {...provided.dragHandleProps}
                                      style={{
                                        cursor: "grab",
                                        padding: "8px",
                                        alignSelf: "center",
                                        margin: "0 15px 0 0",
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faGripLines} />
                                    </div>
                                    <div className="flex-grow-1">
                                      {condition.ConditionDetails.length > 0 &&
                                        condition.ConditionDetails.map(
                                          (detail, detailIndex) => (
                                            <div key={detailIndex}>
                                              <div className="row">
                                                <div className={"col-md-2"}>
                                                  <Input
                                                    inputType="select"
                                                    key={detail.TimeOfDay}
                                                    label="Time Of Day"
                                                    value={[detail.TimeOfDay]}
                                                    onChange={(values) => {
                                                      handleConditionDetailChange(
                                                        detailIndex,
                                                        "TimeOfDay",
                                                        values[0],
                                                        index
                                                      );
                                                      handleConditionDetailChange(
                                                        detailIndex,
                                                        "Condition",
                                                        [],
                                                        index
                                                      );
                                                      handleConditionDetailChange(
                                                        detailIndex,
                                                        "ConditionValue",
                                                        [],
                                                        index
                                                      );
                                                    }}
                                                    options={[
                                                      {
                                                        value: "day",
                                                        label: "Day",
                                                      },
                                                      {
                                                        value: "night",
                                                        label: "Night",
                                                      },
                                                      {
                                                        value: "both",
                                                        label: "Day and Night",
                                                      },
                                                    ]}
                                                  />
                                                </div>
                                                <div className={"col-md-3"}>
                                                  <Input
                                                    inputType="select"
                                                    key={detail.TimeOfDay}
                                                    label="Condition Name"
                                                    value={[detail.Condition]}
                                                    onChange={(values) => {
                                                      handleConditionDetailChange(
                                                        detailIndex,
                                                        "Condition",
                                                        values[0],
                                                        index
                                                      );
                                                      handleConditionDetailChange(
                                                        detailIndex,
                                                        "ConditionValue",
                                                        [],
                                                        index
                                                      );

                                                      if (
                                                        detail.selectedCondition
                                                          .windDirections
                                                      ) {
                                                        handleConditionDetailChange(
                                                          detailIndex,
                                                          "ConditionOperator",
                                                          "=",
                                                          index
                                                        );
                                                      }
                                                    }}
                                                    formatOptionLabel={(o) => {
                                                      const icon =
                                                        conditionNames.find(
                                                          (a) =>
                                                            a.label === o.label
                                                        )?.icon;
                                                      return (
                                                        <div className="d-flex align-items-center gap-2">
                                                          {icon && (
                                                            <MyIcon
                                                              width="16"
                                                              height="16"
                                                              Icon={icon}
                                                              className="text-purple bg-transparent"
                                                            />
                                                          )}
                                                          {o.label}
                                                        </div>
                                                      );
                                                    }}
                                                    options={conditionNames
                                                      .filter((c) => {
                                                        const matchesTimeOfDay =
                                                          detail.TimeOfDay ==
                                                          "day"
                                                            ? c.isDay === true
                                                            : c.isDay === false;

                                                        return matchesTimeOfDay;
                                                      })
                                                      .map((c) => ({
                                                        value: c.key,
                                                        label: c.label,
                                                      }))}
                                                  />
                                                </div>

                                                <div
                                                  className={`col-md-2 ${
                                                    detail.selectedCondition
                                                      .windDirections
                                                      ? "d-none"
                                                      : ""
                                                  }`}
                                                >
                                                  <Input
                                                    key={detail.Condition}
                                                    inputType="select"
                                                    label="Condition Operator"
                                                    value={[
                                                      detail.ConditionOperator,
                                                    ]}
                                                    onChange={(values) => {
                                                      if (values[0] == "=") {
                                                        handleConditionDetailChange(
                                                          detailIndex,
                                                          "ConditionValue",
                                                          [],
                                                          index
                                                        );
                                                      }
                                                      handleConditionDetailChange(
                                                        detailIndex,
                                                        "ConditionOperator",
                                                        values[0],
                                                        index
                                                      );
                                                    }}
                                                    options={operators
                                                      .filter((op) => {
                                                        if (
                                                          (detail.Condition ===
                                                            "daySignificantWeatherCode" ||
                                                            detail.Condition ===
                                                              "nightSignificantWeatherCode") &&
                                                          op.symbol ===
                                                            "between"
                                                        ) {
                                                          return false;
                                                        }

                                                        return true;
                                                      })
                                                      .map((op) => {
                                                        if (
                                                          op.symbol == "between"
                                                        ) {
                                                          return {
                                                            value: op.symbol,
                                                            label: op.desc,
                                                          };
                                                        }
                                                        return {
                                                          value: op.symbol,
                                                          label:
                                                            op.desc +
                                                            " (" +
                                                            op.symbol +
                                                            ")",
                                                        };
                                                      })}
                                                    isDisabled={
                                                      detail.selectedCondition
                                                        .windDirections
                                                    }
                                                  />
                                                </div>

                                                <div className="col-md-3">
                                                  <WeatherConditionValueSelector
                                                    detail={detail}
                                                    detailIndex={detailIndex}
                                                    handleConditionDetailChange={
                                                      handleConditionDetailChange
                                                    }
                                                    index={index}
                                                  />
                                                </div>

                                                <div className="col-md-2">
                                                  <div className="row">
                                                    <div className="col-auto pt-4">
                                                      <button
                                                        onClick={() =>
                                                          detailIndex > 0
                                                            ? removeConditionDetail(
                                                                detailIndex,
                                                                index
                                                              )
                                                            : addConditionDetail(
                                                                index
                                                              )
                                                        }
                                                        type="button"
                                                        className={`btn ${
                                                          detailIndex > 0
                                                            ? "btn-danger"
                                                            : "btn-primary"
                                                        }`}
                                                      >
                                                        <FontAwesomeIcon
                                                          icon={
                                                            detailIndex > 0
                                                              ? faTrash
                                                              : faPlus
                                                          }
                                                        />
                                                      </button>
                                                    </div>
                                                    <div
                                                      className="col-auto pt-1"
                                                      hidden={detailIndex > 0}
                                                    >
                                                      <ColorPicker
                                                        value={condition.Color}
                                                        onColorSelect={(
                                                          color
                                                        ) =>
                                                          handleConditionChange(
                                                            "Color",
                                                            color,
                                                            index
                                                          )
                                                        }
                                                      />
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>

                                              <div className="row mb-2 mt-1">
                                                <div
                                                  className={
                                                    detail.selectedCondition
                                                      .windDirections
                                                      ? "col-md-5"
                                                      : "col-md-7"
                                                  }
                                                >
                                                  {renderCondition(
                                                    detail.selectedCondition,
                                                    detail.TimeOfDay
                                                  )}
                                                </div>
                                                <div className="col-md-3">
                                                  <span className="text-muted">
                                                    {detail.conditionUnit}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          )
                                        )}
                                      <div className="row mt-3">
                                        <div className="col-12">
                                          <Input
                                            inputType="textArea"
                                            label="Condition Description"
                                            value={[condition.Description]}
                                            onChange={(values) =>
                                              handleConditionChange(
                                                "Description",
                                                values[0],
                                                index
                                              )
                                            }
                                            options={[{ value: "", label: "" }]}
                                          />
                                        </div>
                                      </div>
                                      <div className="col-12 mt-3">
                                        <InputLabel>
                                          Days in future to look for weather
                                          alerts
                                        </InputLabel>
                                        <Input
                                          inputType="select"
                                          isMulti
                                          value={
                                            condition.DaysAhead || [
                                              0, 1, 2, 3, 4, 5, 6,
                                            ]
                                          }
                                          onChange={(values) => {
                                            if (values.length === 0) {
                                              handleConditionChange(
                                                "DaysAhead",
                                                [0, 1, 2, 3, 4, 5, 6],
                                                index
                                              );
                                              return;
                                            }
                                            handleConditionChange(
                                              "DaysAhead",
                                              values,
                                              index
                                            );
                                          }}
                                          options={[
                                            { value: 0, label: "Today" },
                                            { value: 1, label: "Tomorrow" },
                                            {
                                              value: 2,
                                              label: "Two Days Ahead",
                                            },
                                            {
                                              value: 3,
                                              label: "Three Days Ahead",
                                            },
                                            {
                                              value: 4,
                                              label: "Four Days Ahead",
                                            },
                                            {
                                              value: 5,
                                              label: "Five Days Ahead",
                                            },
                                            {
                                              value: 6,
                                              label: "Six Days Ahead",
                                            },
                                          ]}
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-danger m-3"
                                        style={{
                                          position: "absolute",
                                          right: 0,
                                          top: 0,
                                        }}
                                        onClick={() =>
                                          handleRemoveCondition(index)
                                        }
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </div>
              <div className=" p-3 mb-4">
                <div className="col-12 ">
                  <h4>Add New Condition</h4>
                </div>

                {newCondition.ConditionDetails.length > 0 &&
                  newCondition.ConditionDetails.map((detail, index) => (
                    <div key={index}>
                      <div className="row">
                        <div className={"col-md-2"}>
                          <Input
                            inputType="select"
                            key={detail.TimeOfDay}
                            label="Time Of Day"
                            value={[detail.TimeOfDay]}
                            onChange={(values) => {
                              handleConditionDetailChange(
                                index,
                                "TimeOfDay",
                                values[0]
                              );
                              handleConditionDetailChange(
                                index,
                                "Condition",
                                ""
                              );
                              handleConditionDetailChange(
                                index,
                                "COnditionValue",
                                []
                              );
                            }}
                            options={[
                              { value: "day", label: "Day" },
                              { value: "night", label: "Night" },
                              { value: "both", label: "Day and Night" },
                            ]}
                          />
                        </div>
                        <div className={"col-md-3"}>
                          <Input
                            inputType="select"
                            key={detail.TimeOfDay}
                            label="Condition Name"
                            value={[detail.Condition]}
                            onChange={(values) => {
                              handleConditionDetailChange(
                                index,
                                "Condition",
                                values[0]
                              );
                              handleConditionDetailChange(
                                index,
                                "ConditionValue",
                                []
                              );

                              if (detail.selectedCondition.windDirections) {
                                handleConditionDetailChange(
                                  index,
                                  "ConditionOperator",
                                  "="
                                );
                              }
                            }}
                            formatOptionLabel={(o) => {
                              const icon = conditionNames.find(
                                (a) => a.label === o.label
                              )?.icon;
                              return (
                                <div className="d-flex align-items-center gap-2">
                                  {icon && (
                                    <MyIcon
                                      width="16"
                                      height="16"
                                      Icon={icon}
                                      className="text-purple bg-transparent"
                                    />
                                  )}
                                  {o.label}
                                </div>
                              );
                            }}
                            options={conditionNames
                              .filter((c) => {
                                const matchesTimeOfDay =
                                  detail.TimeOfDay == "day"
                                    ? c.isDay === true
                                    : c.isDay === false;

                                return matchesTimeOfDay;
                              })
                              .map((c) => ({ value: c.key, label: c.label }))}
                          />
                        </div>

                        <div
                          className={`col-md-2 ${
                            detail.selectedCondition.windDirections
                              ? "d-none"
                              : ""
                          }`}
                        >
                          <Input
                            inputType="select"
                            key={detail.Condition}
                            label="Condition Operator"
                            value={[detail.ConditionOperator]}
                            onChange={(values) => {
                              if (values[0] == "=") {
                                handleConditionDetailChange(
                                  index,
                                  "ConditionValue",
                                  []
                                );
                              }
                              handleConditionDetailChange(
                                index,
                                "ConditionOperator",
                                values[0]
                              );
                            }}
                            options={operators
                              .filter((op) => {
                                if (
                                  (detail.Condition ===
                                    "daySignificantWeatherCode" ||
                                    detail.Condition ===
                                      "nightSignificantWeatherCode") &&
                                  op.symbol === "between"
                                ) {
                                  return false;
                                }

                                return true;
                              })
                              .map((op) => {
                                if (op.symbol == "between") {
                                  return { value: op.symbol, label: op.desc };
                                }
                                return {
                                  value: op.symbol,
                                  label: op.desc + " (" + op.symbol + ")",
                                };
                              })}
                            isDisabled={detail.selectedCondition.windDirections}
                          />
                        </div>

                        <div className="col-md-3">
                          <WeatherConditionValueSelector
                            detail={detail}
                            detailIndex={index}
                            handleConditionDetailChange={
                              handleConditionDetailChange
                            }
                          />
                        </div>

                        <div className="col-md-2">
                          <div className="row">
                            <div className="col-auto pt-4">
                              <button
                                onClick={() =>
                                  index > 0
                                    ? removeConditionDetail(index)
                                    : addConditionDetail()
                                }
                                type="button"
                                className={`btn ${
                                  index > 0 ? "btn-danger" : "btn-primary"
                                }`}
                              >
                                <FontAwesomeIcon
                                  icon={index > 0 ? faTrash : faPlus}
                                />
                              </button>
                            </div>
                            <div className="col-auto pt-1" hidden={index > 0}>
                              <ColorPicker
                                onColorSelect={(color) =>
                                  handleConditionChange("Color", color)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row mb-2 mt-1">
                        <div
                          className={
                            detail.selectedCondition.windDirections
                              ? "col-md-5"
                              : "col-md-7"
                          }
                        >
                          {renderCondition(
                            detail.selectedCondition,
                            detail.TimeOfDay
                          )}
                        </div>
                        <div className="col-md-3">
                          <span className="text-muted">
                            {detail.conditionUnit}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}

                <div className="col-12 mt-3">
                  <Input
                    inputType="textArea"
                    label="Condition Description"
                    value={[newCondition.Description]}
                    onChange={(values) =>
                      handleConditionChange("Description", values[0])
                    }
                    options={[{ value: "", label: "" }]}
                  />
                </div>

                <div className="col-12 mt-3">
                  <InputLabel>
                    Days in future to look for weather alerts{" "}
                  </InputLabel>
                  <Input
                    inputType="select"
                    isMulti
                    value={newCondition.DaysAhead || [0, 1, 2, 3, 4, 5, 6]}
                    onChange={(values) => {
                      if (values.length === 0) {
                        handleConditionChange(
                          "DaysAhead",
                          [0, 1, 2, 3, 4, 5, 6]
                        );
                        return;
                      }
                      handleConditionChange("DaysAhead", values);
                    }}
                    options={[
                      { value: 0, label: "Today" },
                      { value: 1, label: "Tomorrow" },
                      { value: 2, label: "Two Days Ahead" },
                      { value: 3, label: "Three Days Ahead" },
                      { value: 4, label: "Four Days Ahead" },
                      { value: 5, label: "Five Days Ahead" },
                      { value: 6, label: "Six Days Ahead" },
                    ]}
                  />
                </div>

                <div className="col-12 mt-3">
                  <WhiteButton
                    as="button"
                    type="button"
                    icon={() => {
                      return (
                        <FontAwesomeIcon
                          // fontSize={"xs"}
                          icon={faPlus}
                        />
                      );
                    }}
                    heading="Add Condition"
                    subheading="Add a new alert condition"
                    onClick={addCondition}
                    disabled={!isConditionValid}
                  />
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="p-4 mb-4">
              <div className="my-3 g-3 p-3">
                <div className="col-12">
                  <h4>Selected Component</h4>
                  {projects.length > 0 && (
                    <div className="row">
                      {projects.map((proj, index) => {
                        const details =
                          detailsMap[proj.ProjectId] || proj.selectedProject;
                        return (
                          <div className="col-md-3" key={index}>
                            <div className="card">
                              <div className="card-body">
                                <h5 className="card-title">{details.Label}</h5>
                                <div className="my-2 g-1">
                                  <div className="card-text">
                                    Description:
                                    {details.Description}
                                  </div>

                                  <div className="card-text">
                                    Status: {details.Status}
                                  </div>
                                  <div className="card-text">
                                    Selected Input Argument:
                                    <strong className="ms-1">
                                      {details && details.InputArguments
                                        ? details.InputArguments.find(
                                            (inputArg) =>
                                              inputArg.UUID ===
                                              proj.InputArgumentUuid
                                          )?.Label ||
                                          "No matching variables"
                                        : "No variables"}
                                    </strong>
                                  </div>
                                </div>

                                <div className="d-flex justify-content-between">
                                  <Button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      var removedProject = projects[index];
                                      setProjects(
                                        projects.filter((_, i) => i !== index)
                                      );

                                      if (removedProject.ProjectId) {
                                        setProjectList((prevList) => {
                                          if (
                                            !prevList.find(
                                              (p) =>
                                                p.ID ===
                                                removedProject.ProjectId
                                            )
                                          ) {
                                            return [
                                              ...prevList,
                                              removedProject.selectedProject,
                                            ];
                                          }
                                          return prevList;
                                        });
                                      }
                                      setRefreshKey((prev) => prev + 1);
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="p-3 g-3">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <h5 className="card-title">Add New Component</h5>
                      <p className="card-text">
                        Select the component that this alert is associated with
                      </p>
                      <div className="row">
                        <div className="col-md-6">
                          <InputLabel>Select a Component</InputLabel>
                          <AsyncSelect
                            key={refreshKey}
                            loadOptions={getProjects}
                            formatOptionLabel={(o) => {
                              console.log(o);
                              return (
                                <div className="">
                                  {o.label}
                                  <div>
                                    <div>
                                      <span className="opacity-50 me-2">
                                        Status:
                                      </span>
                                      {o.data?.Status}
                                    </div>
                                    {o.data?.Description}
                                  </div>
                                  <div>
                                    <span className="opacity-50 me-2">
                                      Created by:
                                    </span>
                                    {o.data?.CreatedBy}
                                  </div>

                                  <div>
                                    <span className="opacity-50 me-2">
                                      Created at:
                                    </span>
                                    {new Date(
                                      o.data?.CreatedAt
                                    ).toLocaleString()}
                                  </div>
                                </div>
                              );
                            }}
                            placeholder="Search or select a component"
                            onChange={(selectedOption) => {
                              return handleProjectChange(
                                "ProjectId",
                                selectedOption?.value
                              );
                            }}
                            isClearable
                            isSearchable
                            defaultOptions
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </div>
                        <div className="col-md-3">
                          <InputLabel>Select Input Argument</InputLabel>
                          <Select
                            key={newProject.ProjectId}
                            onChange={(selectedOption) =>
                              handleProjectChange(
                                "InputArgumentUuid",
                                selectedOption?.value
                              )
                            }
                            options={
                              newProject.selectedProject.InputArguments?.map(
                                (inputArg) => ({
                                  value: inputArg.UUID,
                                  label: inputArg.Label,
                                })
                              ) || []
                            }
                            defaultOptions={
                              newProject.selectedProject.InputArguments?.map(
                                (inputArg) => ({
                                  value: inputArg.UUID,
                                  label: inputArg.Label,
                                })
                              ) || []
                            }
                            placeholder="Select Input Argument"
                            isDisabled={
                              !newProject.ProjectId ||
                              projects.some(
                                (p) => p.ProjectId === newProject.ProjectId
                              )
                            }
                            isClearable
                            isSearchable
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </div>
                        <div className="col-md-3 mt-4">
                          <Button
                            type="button"
                            className="btn-success"
                            onClick={(e) => {
                              e.preventDefault();
                              setProjects([...projects, newProject]);
                              setProjectList((prevList) =>
                                prevList.filter(
                                  (p) => p.ID !== newProject.ProjectId
                                )
                              );
                              setNewProject({
                                ID: null,
                                InputArgumentUuid: "",
                                selectedProject: {},
                              });
                              setRefreshKey((prev) => prev + 1);
                              projectList.filter(
                                (_, i) => i !== newProject.ProjectId
                              );
                            }}
                            disabled={
                              !newProject.ProjectId ||
                              !newProject.InputArgumentUuid ||
                              newProject.selectedProject?.InputArguments.find(
                                (ia) => ia.UUID !== newProject.InputArgumentUuid
                              )
                            }
                          >
                            Add Component
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </form>
      <JsonCollapse json={conditions} />
    </div>
  );
}
